import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BrandMark from '../components/BrandMark';
import './Landing.css';

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="landing">
        <div className="landing-loading" />
      </div>
    );
  }
  if (user) {
    return null;
  }

  return (
    <div className="landing">
      <header className="landing-hero">
        <div className="landing-hero-inner">
          <div className="landing-logo">
            <BrandMark size={56} />
          </div>
          <h1 className="landing-title">GitHub Access Manager</h1>
          <p className="landing-tagline">
            Request company repository access with a Service Desk ticket. A manager or admin approves it, GitHub access is granted, and the ticket is marked resolved.
          </p>
          <div className="landing-cta">
            <Link to="/login" className="landing-btn landing-btn-primary">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      <section className="landing-flow">
        <div className="landing-features-inner">
          <h2 className="landing-features-title">How access is granted</h2>
          <ol className="landing-steps">
            <li>
              <span className="landing-step-num">1</span>
              <div>
                <strong>File a Service Desk ticket</strong>
                <p>Use Repo access to grant an existing repository, or Create repository to ask for a new one. Include owner/repo, the GitHub username, and the email.</p>
              </div>
            </li>
            <li>
              <span className="landing-step-num">2</span>
              <div>
                <strong>The request opens here</strong>
                <p>Access requests wait for review. Creation requests wait for review before the repository is created on GitHub.</p>
              </div>
            </li>
            <li>
              <span className="landing-step-num">3</span>
              <div>
                <strong>A manager or admin approves</strong>
                <p>Approvers use Access Requests. Approve & grant access adds the person on GitHub. Approve & create repository creates the repo, then grants access.</p>
              </div>
            </li>
            <li>
              <span className="landing-step-num">4</span>
              <div>
                <strong>The ticket is resolved</strong>
                <p>After access is granted, the Service Desk ticket is marked resolved. The user can see the repository and when that access expires.</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section className="landing-features">
        <div className="landing-features-inner">
          <h2 className="landing-features-title">What you can do</h2>
          <ul className="landing-features-list">
            <li className="landing-feature">
              <span className="landing-feature-icon">📦</span>
              <div>
                <strong>Company repositories</strong>
                <p>Browse the company GitHub account’s repositories, request access, and open a repo to see who already has it.</p>
              </div>
            </li>
            <li className="landing-feature">
              <span className="landing-feature-icon">📋</span>
              <div>
                <strong>Approvals</strong>
                <p>Managers and admins approve or reject requests. Approval grants read, write, or admin on GitHub.</p>
              </div>
            </li>
            <li className="landing-feature">
              <span className="landing-feature-icon">⏳</span>
              <div>
                <strong>Access expiry</strong>
                <p>Each grant can expire on a set date. People see that date on their repositories, and access is removed when it ends.</p>
              </div>
            </li>
            <li className="landing-feature">
              <span className="landing-feature-icon">🚫</span>
              <div>
                <strong>Revoke in both places</strong>
                <p>Revoking access in the portal removes the GitHub collaborator as well. The portal record stays until GitHub confirms the removal.</p>
              </div>
            </li>
            <li className="landing-feature">
              <span className="landing-feature-icon">✅</span>
              <div>
                <strong>Access review campaigns</strong>
                <p>Admins start a review of current access. Reviewers certify what should stay. Completing the campaign revokes what was flagged.</p>
              </div>
            </li>
            <li className="landing-feature">
              <span className="landing-feature-icon">🔐</span>
              <div>
                <strong>Accounts and sign-in</strong>
                <p>Sign in with email or GitHub. Optional MFA, notifications, and admin tools for users, activity, and policies.</p>
              </div>
            </li>
          </ul>
          <div className="landing-cta-secondary">
            <Link to="/login" className="landing-btn landing-btn-secondary">
              Sign in to get started
            </Link>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <p>Need an account? Contact your administrator. You'll receive your login credentials by email.</p>
      </footer>
    </div>
  );
};

export default Landing;
